import { hasAnimation } from '@slideforge/shared';
import { useStrings } from '../i18n/context.js';
import type { SlideForgeState } from '../state/useSlideForge.js';

export function DeckPanel({ state }: { readonly state: SlideForgeState }) {
  const t = useStrings();
  const { deck, selectedSlideId, setSelectedSlideId, readDeck, course, slides, slidesAreRemembered } =
    state;
  const parsed = deck.parsed;

  return (
    <section aria-labelledby="sf-deck-heading" className="sf-panel">
      <h2 id="sf-deck-heading">{t.deckHeading}</h2>

      <button type="button" className="sf-primary" onClick={() => void readDeck()} disabled={deck.status === 'reading'}>
        {deck.status === 'reading' ? t.readingDeck : parsed ? t.rereadDeck : t.readDeck}
      </button>

      {deck.status === 'reading' && deck.progress && (
        <div
          className="sf-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={deck.progress.sliceCount}
          aria-valuenow={deck.progress.slicesReceived}
          aria-label={t.readingDeck}
        >
          <div
            className="sf-progress-fill"
            style={{ width: `${(deck.progress.slicesReceived / deck.progress.sliceCount) * 100}%` }}
          />
        </div>
      )}

      {deck.status === 'error' && (
        <p role="alert" className="sf-error">
          {deck.error}
        </p>
      )}

      {parsed && (
        <dl className="sf-stats">
          <Stat label={t.deckSlides} value={String(parsed.deck.slides.length)} />
          <Stat label={t.deckParts} value={String(parsed.deck.media.length + parsed.deck.slides.length)} />
          <Stat label={t.deckSize} value={formatBytes(deck.byteLength)} />
          <Stat label={t.deckMedia} value={String(parsed.deck.media.length)} />
          <Stat
            label={t.deckAnimated}
            value={String(parsed.deck.slides.filter((s) => hasAnimation(s.timing)).length)}
          />
          <Stat label={t.deckElapsed} value={`${Math.round(parsed.elapsedMs)} ms`} />
        </dl>
      )}

      {/*
        The slide list is deliberately outside the `parsed` guard. It renders
        from the remembered index too, so reopening the pane shows the deck and
        its quiz badges straight away instead of an empty panel that looks like
        the author's work is gone.
      */}
      {slides.length > 0 && (
        <>
          <h3 id="sf-slide-list">{t.slideList}</h3>
          {slidesAreRemembered && <p className="sf-note">{t.rememberedSlides}</p>}
          <ol className="sf-slides" aria-labelledby="sf-slide-list">
            {slides.map((slide) => {
              const hasQuiz = course.slides[slide.id]?.quiz !== undefined;
              const isSelected = slide.id === selectedSlideId;
              return (
                <li key={slide.id}>
                  <button
                    type="button"
                    aria-current={isSelected ? 'true' : 'false'}
                    className={isSelected ? 'sf-slide sf-slide-current' : 'sf-slide'}
                    onClick={() => setSelectedSlideId(slide.id)}
                  >
                    <span className="sf-slide-number">{t.slideNumber(slide.index + 1)}</span>
                    <span className="sf-slide-name">{slide.name}</span>
                    {hasQuiz && <span className="sf-badge">{t.hasQuiz}</span>}
                  </button>
                </li>
              );
            })}
          </ol>
        </>
      )}

      {parsed && parsed.diagnostics.length > 0 && (
        <details className="sf-diagnostics">
          <summary>{`${t.validationHeading} (${parsed.diagnostics.length})`}</summary>
          <ul>
            {parsed.diagnostics.map((d, i) => (
              <li key={`${d.code}-${i}`} className={`sf-severity-${d.severity}`}>
                <strong>{t.severityNames[d.severity] ?? d.severity}</strong>
                {': '}
                {d.message}
              </li>
            ))}
          </ul>
        </details>
      )}

    </section>
  );
}

function Stat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="sf-stat">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
